import React, { useState } from 'react';
import type moment from 'moment';
import {
  Banner,
  Brief,
  Button,
  Card,
  CardSection,
  CondensedMedium,
  CondensedLarge,
  ContextMenu,
  ContextMenuItem,
  Flashy,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  ReadonlyTextarea,
  ToastUtil,
  useApi,
  Grid,
  GridColumn,
  GridRow,
} from '@axiom/ui';
import { isEnvoyAdmin, isEnvoyUser } from '@axiom/utils';
import type { Experience, Taxonomy } from '@axiom/validation';

import { DateUtil } from '../../utils/date-util';
import { ExperiencesApi } from '../../api/experiences-api';
import { CandidateExperiencesApi } from '../../api/candidate-experiences-api';
import { CandidateIndustriesApi } from '../../api/candidate-industries-api';
import { UserApi } from '../../api/user-api';
import { ConvertToAxiomExperience } from '../ConvertToAxiomExperience/ConvertToAxiomExperience';

type CandidateExperienceItemProps = {
  expanded?: boolean;
  experience: Experience;
  talentName: string;
  taxonomy: Taxonomy;
  onEditExperience?: (experience: Experience) => void;
  onToggleExpanded?: (expanded: boolean) => void;
};

export const CandidateExperienceItem = ({
  expanded = false,
  experience,
  talentName,
  taxonomy,
  onEditExperience,
  onToggleExpanded,
}: CandidateExperienceItemProps) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isConvertingExperience, setIsConvertingExperience] = useState(false);

  const [{ data: user }] = useApi(UserApi.readSessionUser());

  if (!experience.id) return null;

  const {
    client,
    endDate,
    exceptionApprovedAt,
    exceptionRequestedAt,
    expectedDescriptionLength,
    externalOpportunityName,
    isAxiom,
    locationName,
    startDate,
    description,
    opportunity,
    industryValue,
    id,
  } = experience;

  const formatEndDate = (dateString: string, isAxiomExp: boolean) => {
    const momentDate = DateUtil.castAsMoment(
      dateString
    ) as moment.Moment | null;
    if (momentDate?.isSameOrAfter(new Date())) {
      return 'Present';
    }

    return isAxiomExp
      ? DateUtil.displayDate(dateString)
      : DateUtil.displayMonthYear(dateString);
  };

  const cancelDelete = () => {
    setIsConfirmingDelete(false);
  };

  const confirmDelete = async () => {
    await ExperiencesApi.deleteExperience(experience.id);
    await CandidateExperiencesApi.refreshExperiences(experience.candidateId);
    await CandidateIndustriesApi.refreshIndustries(experience.candidateId);
  };

  const startEditing = () => {
    onEditExperience?.(experience);
  };

  return (
    <Gutter vertical="10px" data-test="EXPERIENCELOGITEM" data-value={client}>
      <Card>
        <CardSection divider={expanded}>
          <Grid>
            <GridRow>
              <GridColumn largeScreenWidth={6}>
                <CondensedMedium name="COMPANY_LABEL">COMPANY</CondensedMedium>
                <CondensedLarge name="COMPANY_VALUE">{client}</CondensedLarge>
              </GridColumn>
              <GridColumn largeScreenWidth={6}>
                <Layout
                  direction="horizontal"
                  horizontalGutter="16px"
                  position="middle"
                >
                  <LayoutItem fluid>
                    <CondensedMedium name="DATE_LABEL">DATE</CondensedMedium>
                    <CondensedLarge name="DATE_VALUE">
                      <time dateTime={startDate}>
                        {isAxiom
                          ? DateUtil.displayDate(startDate)
                          : DateUtil.displayMonthYear(startDate)}
                      </time>
                      {' - '}
                      {endDate ? (
                        <time dateTime={endDate}>
                          {formatEndDate(endDate, isAxiom)}
                        </time>
                      ) : (
                        <span>Present</span>
                      )}
                    </CondensedLarge>
                  </LayoutItem>
                  <LayoutItem>
                    <ContextMenu direction="left">
                      <ContextMenuItem
                        name="TALENT_EXPERIENCE_CONTEXT_MENU_ITEM_EDIT"
                        onClick={startEditing}
                      >
                        Edit
                      </ContextMenuItem>
                      <ContextMenuItem
                        name="TALENT_EXPERIENCE_CONTEXT_MENU_ITEM_DELETE"
                        onClick={() => {
                          setIsConfirmingDelete(true);
                        }}
                      >
                        Delete
                      </ContextMenuItem>
                      {!isAxiom &&
                        (isEnvoyUser(user) || isEnvoyAdmin(user)) && (
                          <ContextMenuItem
                            name="TALENT_EXPERIENCE_CONTEXT_MENU_ITEM_CONVERT"
                            onClick={() => {
                              setIsConvertingExperience(true);
                            }}
                          >
                            Convert
                          </ContextMenuItem>
                        )}
                    </ContextMenu>
                  </LayoutItem>
                </Layout>
              </GridColumn>

              <GridColumn largeScreenWidth={12}>
                <Gutter top="18px">
                  <CondensedMedium name="OPPORTUNITY_LABEL">
                    {isAxiom ? 'OPPORTUNITY' : 'JOB TITLE'}
                  </CondensedMedium>
                  <CondensedLarge name="OPPORTUNITY_VALUE" data-value={id}>
                    {isAxiom
                      ? opportunity?.jobName || '--'
                      : externalOpportunityName || '--'}
                  </CondensedLarge>
                </Gutter>
              </GridColumn>
            </GridRow>
          </Grid>
        </CardSection>

        <Brief
          key={`brief-${experience.id}-${expanded ? 'expanded' : 'collapsed'}`}
          name="EXPERIENCE_BRIEF"
          briefMaxChildren={1}
          pattern="secondary"
          position="center"
          expanded={expanded}
          onToggle={onToggleExpanded}
        >
          <div />
          <CardSection>
            <Grid>
              <GridRow>
                <GridColumn largeScreenWidth={6}>
                  <CondensedMedium name="LOCATION_LABEL">
                    LOCATION
                  </CondensedMedium>
                  <CondensedLarge name="LOCATION_VALUE">
                    {isAxiom
                      ? opportunity?.locationCityStateCountry || '--'
                      : locationName || '--'}
                  </CondensedLarge>
                </GridColumn>
                <GridColumn largeScreenWidth={6}>
                  <CondensedMedium name="INDUSTRY_LABEL">
                    INDUSTRY
                  </CondensedMedium>
                  <CondensedLarge name="INDUSTRY_VALUE">
                    {(industryValue &&
                      taxonomy.industries[industryValue]?.displayValue) ||
                      '--'}
                  </CondensedLarge>
                </GridColumn>
              </GridRow>

              {isAxiom ? (
                <GridRow>
                  <GridColumn largeScreenWidth={12}>
                    <Gutter top="18px">
                      <CondensedMedium name="SITE_PREFERENCE_LABEL">
                        SITE PREFERENCE
                      </CondensedMedium>
                      <CondensedLarge name="SITE_PREFERENCE_VALUE">
                        {opportunity?.position?.worksite || '--'}
                      </CondensedLarge>
                    </Gutter>
                  </GridColumn>
                </GridRow>
              ) : null}

              <GridRow>
                <GridColumn largeScreenWidth={12}>
                  <Gutter top="18px">
                    <CondensedMedium name="DESCRIPTION_LABEL">
                      DESCRIPTION
                    </CondensedMedium>
                    <ReadonlyTextarea
                      value={description || '--'}
                      formatting="modern"
                    />
                  </Gutter>

                  {exceptionRequestedAt && !exceptionApprovedAt ? (
                    <Gutter top="16px">
                      <Banner name="DESCRIPTION_LENGTH_EXCEPTION_REQUESTED_BANNER">
                        <Flashy bold>Exception requested.</Flashy> This
                        description is under {expectedDescriptionLength}{' '}
                        characters. Help talent expand the description to meet
                        the requirement or approve as is.
                        <Gutter top="8px">
                          <Button
                            name="DESCRIPTION_LENGTH_EXCEPTION_APPROVE_BUTTON"
                            onClick={async () => {
                              await ExperiencesApi.updateExperience(
                                experience.id,
                                {
                                  exceptionApproved: true,
                                }
                              );
                              await CandidateExperiencesApi.refreshExperiences(
                                experience.candidateId
                              );
                              ToastUtil.add({
                                name: 'DESCRIPTION_LENGTH_EXCEPTION_APPROVED_TOAST',
                                children: (
                                  <CondensedLarge>
                                    You have approved {talentName}'s experience
                                    description.
                                  </CondensedLarge>
                                ),
                              });
                            }}
                          >
                            Approve
                          </Button>
                        </Gutter>
                      </Banner>
                    </Gutter>
                  ) : null}
                </GridColumn>
              </GridRow>
            </Grid>
          </CardSection>
        </Brief>
      </Card>

      {isConfirmingDelete ? (
        <Modal size="confirm" name="CONFIRMEXPDELETE">
          <ModalHeader onClose={cancelDelete}>
            Delete "{isAxiom ? opportunity?.jobName : externalOpportunityName}"
          </ModalHeader>
          <ModalSection>
            <Paragraph>
              You have chosen to delete this experience. Doing so will remove it
              from Axiom For Talent as well. Are you sure you would like to
              proceed?
            </Paragraph>
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL_DELETE_BUTTON"
              variation="outline"
              onClick={cancelDelete}
            >
              Cancel
            </Button>
            <Button name="CONFIRM_DELETE_BUTTON" onClick={confirmDelete}>
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
      {isConvertingExperience ? (
        <ConvertToAxiomExperience
          experience={experience}
          onClose={() => {
            setIsConvertingExperience(false);
          }}
        />
      ) : null}
    </Gutter>
  );
};
